'use client';

import { useEffect, useRef } from 'react';

import styles from './floating-site-chrome.module.css';

export function AtmosphericParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const doc = canvas.ownerDocument;
    const view = doc.defaultView;
    if (!view) return;
    const motion = view.matchMedia('(prefers-reduced-motion: reduce)');
    let width = view.innerWidth;
    let height = view.innerHeight;
    let frame: number | undefined;
    let intersecting = true;
    let lastY = view.scrollY;
    let scrollVelocity = 0;
    let decay = 0;
    const particles = Array.from({ length: Math.min(90, Math.floor(width / 16)) }, () => ({
      x: Math.random() * width, y: Math.random() * height,
      r: Math.random() * 1.6 + 0.3,
      vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.15,
      alpha: Math.random() * 0.4 + 0.1,
    }));
    const resize = () => {
      width = view.innerWidth;
      height = view.innerHeight;
      const dpr = Math.min(view.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const render = () => {
      decay += (scrollVelocity - decay) * 0.08;
      scrollVelocity *= 0.9;
      ctx.clearRect(0, 0, width, height);
      const push = decay * 0.02;
      for (const particle of particles) {
        particle.x += particle.vx + push * particle.r * 2;
        particle.y += particle.vy + push * 0.5;
        if (particle.x < -10) particle.x = width + 10;
        if (particle.x > width + 10) particle.x = -10;
        if (particle.y < -10) particle.y = height + 10;
        if (particle.y > height + 10) particle.y = -10;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(166, 109, 88, ${particle.alpha + decay * 0.004})`;
        ctx.fill();
      }
      frame = view.requestAnimationFrame(render);
    };
    const sync = () => {
      if (frame !== undefined) view.cancelAnimationFrame(frame);
      frame = undefined;
      lastY = view.scrollY;
      scrollVelocity = 0;
      decay = 0;
      if (!motion.matches && !doc.hidden && intersecting) frame = view.requestAnimationFrame(render);
      else ctx.clearRect(0, 0, width, height);
    };
    const onScroll = () => {
      scrollVelocity = Math.min(Math.abs(view.scrollY - lastY), 40);
      lastY = view.scrollY;
    };
    const observer = typeof view.IntersectionObserver === 'undefined' ? undefined : new view.IntersectionObserver(([entry]) => {
      intersecting = entry?.isIntersecting ?? false;
      sync();
    });
    observer?.observe(canvas.parentElement ?? canvas);
    resize();
    sync();
    motion.addEventListener('change', sync);
    doc.addEventListener('visibilitychange', sync);
    view.addEventListener('scroll', onScroll, { passive: true });
    view.addEventListener('resize', resize);
    return () => {
      if (frame !== undefined) view.cancelAnimationFrame(frame);
      observer?.disconnect();
      motion.removeEventListener('change', sync);
      doc.removeEventListener('visibilitychange', sync);
      view.removeEventListener('scroll', onScroll);
      view.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className={styles.particles} aria-hidden="true" />;
}
